import ArrowDown from '@src/svgs/ArrowDown';
import ArrowUp from '@src/svgs/ArrowUp';
import CloseIcon from '@src/svgs/CloseIcon';
import SearchIconOutline from '@src/svgs/SearchIconOutline';
import {
  applySearchHighlights,
  applyTranslatedHighlights,
  clearSearchHighlights,
  clearTranslatedHighlights,
  findTranscriptMatchesWithRanges,
  findTranslatedMatches,
  translatedSearchHighlightCss,
} from '@src/utility/transcriptSearchUtils';
import debounce from 'lodash/debounce';
import { observer } from 'mobx-react-lite';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled, { createGlobalStyle } from 'styled-components';

import { useTranslatedElements } from './hooks/useTranslatedElements';
import { useReelTranscriptStructure } from './reelStructure/ReelTranscriptStructureContext';
import { revealCollapsedWord } from './reelStructure/revealCollapsedWord';

import type { TranscriptElement } from '../../types.ts/transcript';
import type {
  SearchMatch,
  TranslatedSearchMatch,
} from '@src/utility/transcriptSearchUtils';

/** Global stylesheet rule for the translated-search CSS Custom Highlight
 *  pseudo-elements. Mounted once per `TranscriptSearch` instance — the rule
 *  itself is idempotent (same selectors and colors), so multiple instances
 *  on the page won't clash. */
const TranslatedSearchHighlightStyle = createGlobalStyle`
  ${translatedSearchHighlightCss}
`;

type Props = {
  transcriptElements: TranscriptElement[] | undefined;
  scrollRef: React.RefObject<HTMLDivElement>;
  /** When true, also search removed/cut elements (for redline view) */
  includeRemovedElements?: boolean;
  /** Active translation language. When non-null, search runs against the
   *  translated text (phrase `translatedValue`s) instead of the original
   *  transcript and highlights are painted via the CSS Custom Highlight API
   *  over the rendered phrase spans. */
  language?: string | null;
  /** Called when the user dismisses the search row (Escape or close button) */
  onClose?: () => void;
};

const TranscriptSearch: React.FC<Props> = observer(function TranscriptSearch({
  transcriptElements,
  scrollRef,
  includeRemovedElements = false,
  language = null,
  onClose,
}) {
  const [query, setQuery] = useState('');
  // The original-language and translated paths produce different match
  // shapes. We only ever populate one set at a time (driven by `language`).
  const [matches, setMatches] = useState<SearchMatch[]>([]);
  const [translatedMatches, setTranslatedMatches] = useState<
    TranslatedSearchMatch[]
  >([]);
  const [activeMatchIndex, setActiveMatchIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const isTranslated = !!language;
  const totalMatches = isTranslated ? translatedMatches.length : matches.length;

  // Compute translated elements when a language is selected. The hook reads
  // observable translation-store state, so this component is wrapped in
  // `observer` to react to translation edits/loads. Returns an empty-ish
  // result when `language` is empty or no translation is loaded; we gate
  // search use on the active language.
  const translatedElements = useTranslatedElements(
    transcriptElements ?? [],
    language ?? '',
    { hideOutOfScopeElements: false },
  );
  // Mirror translated elements through a ref so the search effect doesn't
  // depend on its identity. `useTranslatedElements` is intentionally not
  // memoized (it tracks MobX state), so it returns a fresh array on every
  // render. Treating that as a search dep would re-fire `runSearch` on
  // every render — and `runSearch` resets `activeMatchIndex` to 0 and
  // re-applies highlights with `scrollIntoView`, which (1) undoes the
  // user's down/up navigation, and (2) snaps the viewport back when the
  // user manually scrolls (any scroll triggers a re-render somewhere
  // that propagates here, which would re-run the search and re-scroll).
  const translatedElementsRef = useRef(translatedElements);
  useEffect(() => {
    translatedElementsRef.current = translatedElements;
  });

  // Read by the apply-highlights effect to locate words inside redline block
  // spans. A ref, not a dep: an edit already yields new `matches`, and a dep
  // would re-scroll on every transcript change.
  const transcriptElementsRef = useRef(transcriptElements);
  useEffect(() => {
    transcriptElementsRef.current = transcriptElements;
  });

  // Auto-focus the input when the search row opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Clear highlights on unmount so closing the row always cleans up. Both
  // highlight systems are cleared because a switch between languages while
  // the bar was open could have left state in either.
  useEffect(() => {
    return () => {
      const container = scrollRef.current;
      if (container) clearSearchHighlights(container);
      clearTranslatedHighlights();
    };
  }, [scrollRef]);

  // Intercept native keyboard events so the transcript's own keydown handler
  // (attached via addEventListener) does not receive them while the input is focused.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const stop = (e: Event) => e.stopPropagation();
    wrapper.addEventListener('keydown', stop);
    wrapper.addEventListener('keyup', stop);
    wrapper.addEventListener('keypress', stop);
    return () => {
      wrapper.removeEventListener('keydown', stop);
      wrapper.removeEventListener('keyup', stop);
      wrapper.removeEventListener('keypress', stop);
    };
  }, []);

  // Debounced match computation. Only updates state — does NOT apply
  // highlights or scroll. That's a separate effect below that responds to
  // (matches, activeMatchIndex). Splitting the two means an unrelated
  // re-render that happens to recompute matches won't re-snap the
  // viewport, and a navigation arrow press changes only activeMatchIndex
  // without re-running search.
  const runSearch = useMemo(
    () =>
      debounce(
        (
          term: string,
          elements: TranscriptElement[] | undefined,
          activeLanguage: string | null,
          includeRemoved: boolean,
        ) => {
          if (!term) {
            setMatches([]);
            setTranslatedMatches([]);
            return;
          }

          if (activeLanguage) {
            const matchResults = findTranslatedMatches(
              translatedElementsRef.current,
              term,
              includeRemoved,
            );
            setMatches([]);
            setTranslatedMatches(matchResults);
            return;
          }

          if (!elements?.length) {
            setMatches([]);
            setTranslatedMatches([]);
            return;
          }

          const matchResults = findTranscriptMatchesWithRanges(
            elements,
            term,
            includeRemoved,
          );
          setMatches(matchResults);
          setTranslatedMatches([]);
        },
        250,
      ),
    [],
  );

  // Re-run search when the query, language, base elements, or
  // include-removed flag changes. `translatedElements` is intentionally
  // not in the dep list — read via ref above. (See ref-mirror comment.)
  useEffect(() => {
    runSearch(
      query,
      transcriptElements,
      language ?? null,
      includeRemovedElements,
    );
    return () => runSearch.cancel();
  }, [query, transcriptElements, language, includeRemovedElements, runSearch]);

  // Reset active match to 0 on any change that produces a fresh match set.
  // Without this, navigating to match 2 of "foo" then changing the query
  // would leave activeMatchIndex=1 even though the new match list is
  // shorter or different.
  useEffect(() => {
    setActiveMatchIndex(0);
  }, [query, language, includeRemovedElements]);

  // PLAT-11159 — expand-then-look: when the active match sits inside a
  // folded reel passage, open it BEFORE the highlight effect below goes
  // looking for the word's DOM node. The reveal is a context state change,
  // so it re-renders this component and the effect below re-runs (it lists
  // the collapse state as a dependency) — this time finding the row.
  //
  // The view is mirrored through a ref rather than listed as a dependency,
  // and that is load-bearing: its identity changes on every fold, so a
  // dependency here would re-run this effect when the creator folds the very
  // passage the active match sits in — and immediately reopen it. The
  // creator would be unable to fold a chapter while a search was live, and
  // every attempt would emit an `auto_expanded` event blaming the search.
  // Only a change of TARGET reveals, which is the same rule the cursor path
  // follows for the same reason (a fold is never fought by a re-render).
  const reelView = useReelTranscriptStructure();
  const reelViewRef = useRef(reelView);
  useEffect(() => {
    reelViewRef.current = reelView;
  }, [reelView]);
  useEffect(() => {
    if (isTranslated) return;
    const active = matches[activeMatchIndex];
    if (!active) return;
    revealCollapsedWord(
      reelViewRef.current,
      transcriptElements,
      active.startIndex,
      undefined,
      'search',
    );
  }, [matches, activeMatchIndex, isTranslated, transcriptElements]);

  // Apply highlights when matches or activeMatchIndex change. The
  // highlight utilities also scroll the active match into view, so this
  // effect is what drives the "scroll to next match" behavior on
  // navigation. It does NOT re-run on every render — only when the
  // relevant state actually changes. The reel collapse state is a
  // dependency deliberately: a navigation that opened a folded passage
  // must re-apply once the passage's rows exist.
  useEffect(() => {
    const container = scrollRef.current;
    if (container) clearSearchHighlights(container);
    clearTranslatedHighlights();
    if (!container) return;
    if (isTranslated) {
      if (translatedMatches.length > 0) {
        applyTranslatedHighlights(
          container,
          translatedMatches,
          activeMatchIndex,
        );
      }
    } else {
      if (matches.length > 0) {
        applySearchHighlights(
          container,
          matches,
          activeMatchIndex,
          transcriptElementsRef.current,
        );
      }
    }
  }, [
    matches,
    translatedMatches,
    activeMatchIndex,
    isTranslated,
    scrollRef,
    reelView?.collapse,
  ]);

  const navigateTo = useCallback(
    (newIndex: number) => {
      if (totalMatches === 0) return;
      // Highlight application + scroll-into-view is handled by the
      // apply-highlights effect, which fires when `activeMatchIndex`
      // changes. We don't manually call `applyXHighlights` here — that
      // would race against the effect and re-snap the viewport.
      setActiveMatchIndex(newIndex);
    },
    [totalMatches],
  );

  const goNext = useCallback(() => {
    if (totalMatches === 0) return;
    navigateTo((activeMatchIndex + 1) % totalMatches);
  }, [activeMatchIndex, totalMatches, navigateTo]);

  const goPrev = useCallback(() => {
    if (totalMatches === 0) return;
    navigateTo((activeMatchIndex - 1 + totalMatches) % totalMatches);
  }, [activeMatchIndex, totalMatches, navigateTo]);

  const handleClear = useCallback(() => {
    const container = scrollRef.current;
    if (container) clearSearchHighlights(container);
    clearTranslatedHighlights();
    setQuery('');
    setMatches([]);
    setTranslatedMatches([]);
    setActiveMatchIndex(0);
  }, [scrollRef]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClear();
        inputRef.current?.blur();
        onClose?.();
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (e.shiftKey) goPrev();
        else goNext();
      }
    },
    [handleClear, goNext, goPrev, onClose],
  );

  const stopPropagation = (e: React.SyntheticEvent) => e.stopPropagation();

  return (
    <SearchWrapper
      ref={wrapperRef}
      onClick={stopPropagation}
      onMouseUp={stopPropagation}
      onMouseDown={stopPropagation}
      data-testid="transcript-search-bar"
    >
      {/*
        `createGlobalStyle` injects to <head> regardless of where it's
        mounted in the tree — so this could live anywhere. It's placed
        next to the search-bar JSX for proximity to the rest of the
        translated-search wiring.
      */}
      <TranslatedSearchHighlightStyle />
      <SearchIconOutline />
      <SearchInput
        ref={inputRef}
        type="text"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search..."
        spellCheck={false}
        autoComplete="off"
      />
      {query && (
        <>
          <MatchCount>
            {totalMatches > 0
              ? `${activeMatchIndex + 1} of ${totalMatches}`
              : '0 results'}
          </MatchCount>
          {totalMatches > 0 && (
            <>
              <NavButton
                tabIndex={-1}
                onClick={goPrev}
                title="Previous match (Shift+Enter)"
              >
                <ArrowUp
                  width="8"
                  height="5"
                  viewBox="0 0 18 11"
                  strokeColor="var(--text-muted)"
                  strokeWidth="2.5"
                />
              </NavButton>
              <NavButton
                tabIndex={-1}
                onClick={goNext}
                title="Next match (Enter)"
              >
                <ArrowDown
                  width="8"
                  height="5"
                  viewBox="0 0 18 11"
                  strokeColor="var(--text-muted)"
                  strokeWidth="2.5"
                />
              </NavButton>
            </>
          )}
        </>
      )}
      <ClearButton
        tabIndex={-1}
        aria-label="Clear search"
        onClick={() => {
          handleClear();
          onClose?.();
        }}
      >
        <CloseIcon width={10} height={10} />
      </ClearButton>
    </SearchWrapper>
  );
});

export default TranscriptSearch;

// ---------------------------------------------------------------------------
// Styled components
// ---------------------------------------------------------------------------

const SearchWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 260px;
  height: 32px;
  padding: 0 10px;
  background-color: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  box-shadow: var(--elevation-md);
  box-sizing: border-box;
  transition: border-color var(--transition-fast);

  &:hover {
    border-color: var(--text-muted);
  }

  &:focus-within {
    border-color: var(--accent);
  }
`;

const SearchInput = styled.input`
  flex: 1;
  background: none;
  border: none;
  outline: none;
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  min-width: 0;

  &::placeholder {
    color: var(--text-muted);
  }
`;

const MatchCount = styled.span`
  color: var(--text-muted);
  font-size: 11px;
  white-space: nowrap;
  user-select: none;
`;

const NavButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-input);
  color: var(--text-muted);
  opacity: ${props => (props.disabled ? 0.3 : 1)};
  pointer-events: ${props => (props.disabled ? 'none' : 'auto')};
  transition: all 150ms ease-out;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 2px 4px;
  cursor: pointer;
  color: var(--text-muted);
  display: flex;
  align-items: center;
  border-radius: var(--radius-input);
  transition: all 150ms ease-out;

  &:hover {
    background: var(--surface-elevated);
    color: var(--foreground);
  }
`;
